import { Analytics } from '@analytics';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import CustomizableModal from '../../Common/CustomizableModal';
import messages from './messages';

/*
 * Card 118. Per-guide override of the company's "review page before the tipping
 * page" setting.
 *
 * Three states rather than a switch on purpose: a guide with no override has to
 * stay tied to the company setting, so that a company-wide change still reaches
 * them. A two-state switch would have to pick a side for every guide the first
 * time anyone opened this dialog.
 */
export const REVIEW_FIRST_INHERIT = 'inherit';
export const REVIEW_FIRST_ON = 'on';
export const REVIEW_FIRST_OFF = 'off';

export const toReviewFirstSetting = reviewFirst => {
  if (reviewFirst === true) return REVIEW_FIRST_ON;
  if (reviewFirst === false) return REVIEW_FIRST_OFF;

  return REVIEW_FIRST_INHERIT;
};

const OptionRow = ({ isSelected, label, help, onSelect }) => (
  <button
    type="button"
    role="radio"
    aria-checked={isSelected}
    onClick={onSelect}
    className={`w-full text-left flex gap-3 items-start p-4 rounded-xl border border-solid cursor-pointer transition-colors active:translate-y-[1px] ${
      isSelected
        ? 'border-indigo-600 bg-indigo-50/60'
        : 'border-slate-200 bg-white hover:bg-slate-50'
    }`}>
    <span
      aria-hidden="true"
      className={`mt-0.5 shrink-0 w-4 h-4 rounded-full border-2 border-solid flex items-center justify-center ${
        isSelected ? 'border-indigo-600' : 'border-slate-300'
      }`}>
      {isSelected && <span className="w-2 h-2 rounded-full bg-indigo-600" />}
    </span>
    <span className="min-w-0">
      <span className="block font-semibold text-slate-800">{label}</span>
      <span className="block text-sm text-slate-500 mt-1">{help}</span>
    </span>
  </button>
);

const ReviewFirstModal = ({
  isOpen,
  setIsOpen,
  profile,
  companyDirectToPostTip,
  setReviewFirst
}) => {
  const intl = useIntl();
  const [selected, setSelected] = useState(toReviewFirstSetting(profile?.reviewFirst));

  // The dropdown keeps this modal mounted between guides, so re-sync whenever
  // it reopens or the row refreshes; otherwise it shows the previous guide's
  // choice.
  useEffect(() => {
    setSelected(toReviewFirstSetting(profile?.reviewFirst));
  }, [isOpen, profile?.userId, profile?.reviewFirst]);

  const closeModal = () => setIsOpen(false);

  const guideName = `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim();

  const options = [
    {
      value: REVIEW_FIRST_INHERIT,
      label: intl.formatMessage(messages.inheritLabel),
      help: intl.formatMessage(
        companyDirectToPostTip ? messages.inheritOnHelp : messages.inheritOffHelp
      )
    },
    {
      value: REVIEW_FIRST_ON,
      label: intl.formatMessage(messages.onLabel),
      help: intl.formatMessage(messages.onHelp)
    },
    {
      value: REVIEW_FIRST_OFF,
      label: intl.formatMessage(messages.offLabel),
      help: intl.formatMessage(messages.offHelp)
    }
  ];

  return (
    <CustomizableModal
      modern
      bodyPadding
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      body={
        <div role="radiogroup" aria-label={intl.formatMessage(messages.title)} className="grid gap-3 pb-2">
          {options.map(option => (
            <OptionRow
              key={option.value}
              isSelected={selected === option.value}
              label={option.label}
              help={option.help}
              onSelect={() => setSelected(option.value)}
            />
          ))}
        </div>
      }
      handleFooterCloseButton={closeModal}
      handleHeaderCloseButton
      onClose={closeModal}
      title={intl.formatMessage(messages.title)}
      desc={
        guideName ? (
          <div className="text-slate-600">
            <span className="mr-1">{intl.formatMessage(messages.description)}</span>
            <span className="font-bold text-slate-800">{guideName}</span>
          </div>
        ) : (
          intl.formatMessage(messages.description)
        )
      }
      primaryActionButtonData={{
        isDisabled: selected === toReviewFirstSetting(profile?.reviewFirst),
        onClick: () => {
          setReviewFirst(profile.userId, selected);
          Analytics.track('set guide review first', { setting: selected });
          closeModal();
        },
        content: intl.formatMessage(messages.save)
      }}
    />
  );
};

export default ReviewFirstModal;
